// WhitelabelLandingPage — /whitelabel
// THE white-label landing: for any organization that wants the SWA engine
// under their own brand — universities, businesses, NGOs, tutoring centers.
// One page sells the concept (your brand, our engine, verified evidence);
// the org-type picker inside the signup flow handles the segmentation.
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Building2, GraduationCap, Users, Heart, Globe, Church,
  ShieldCheck, ArrowRight, CheckCircle2, Sparkles, Eye, Lock, Palette,
  Clock, Award, ExternalLink, Menu, X,
} from 'lucide-react'
import SEO from '@/components/layout/SEO'
import { supabase } from '@/integrations/supabase/client'
import { useTranslation } from 'react-i18next'
import LanguagePicker from '@/components/ui/LanguagePicker'

const M = '#06060c'
const D2 = '#0a0a14'
const CARD = '#0d0d15'
const BLUE = '#0ea5e9'
const G = '#d4a843'
const BR = 'rgba(255,255,255,0.10)'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const MU2 = 'rgba(255,255,255,0.35)'
const serif = "'Playfair Display', Georgia, serif"

const ORG_TYPES = [
  { value: 'university', icon: <GraduationCap size={22} />, label: 'University', desc: 'Higher education, continuing studies, professional programs' },
  { value: 'business', icon: <Building2 size={22} />, label: 'Business', desc: 'Employee training, CSR programs, workforce development' },
  { value: 'ngo', icon: <Heart size={22} />, label: 'NGO / Nonprofit', desc: 'Community education, outreach programs, mission work' },
  { value: 'school', icon: <Users size={22} />, label: 'School', desc: 'K-12, academy, micro-school, learning center' },
  { value: 'church', icon: <Church size={22} />, label: 'Church', desc: 'Congregation education, Bible study, discipleship' },
  { value: 'sports_club', icon: <Users size={22} />, label: 'Sports Club / Academy', desc: 'Athlete education, coaching certifications, sports science' },
  { value: 'community', icon: <Globe size={22} />, label: 'Learning Community', desc: 'Tutoring, co-ops, enrichment programs, lifelong learning' },
]

export default function WhitelabelLandingPage() {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [courses, setCourses] = useState<number | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    supabase.from('classes').select('id', { count: 'exact', head: true })
      .eq('is_active', true)
      .eq('facility_id', '00000000-0000-0000-0000-000000000001')
      .then(({ count }) => { if (count) setCourses(count) })
  }, [])

  // Live seat pricing from subscription_plans — digits truthful
  const [seatPrice, setSeatPrice] = useState(15)
  const [schoolPrice, setSchoolPrice] = useState(12)
  useEffect(() => {
    supabase.from('subscription_plans')
      .select('plan_key, price_monthly')
      .in('plan_key', ['wl_business', 'wl_school'])
      .eq('is_active', true)
      .then(({ data }: any) => {
        for (const p of data || []) {
          if (p.plan_key === 'wl_business') setSeatPrice(Number(p.price_monthly) || 15)
          if (p.plan_key === 'wl_school') setSchoolPrice(Number(p.price_monthly) || 12)
        }
      })
  }, [])

  const catalogSize = courses ?? 114

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO
        title="Your Own Learning Platform: Branded Training & Verified Credentials | Still Waters Academy"
        description={`Launch your own branded learning platform with AI tutoring, server-verified assessment, and publicly verifiable credentials. For businesses, NGOs, schools, churches, and sports clubs. From $${schoolPrice} per seat per month.`}
      />

      {/* ═══ NAV ═══ */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(6,6,12,0.95)', backdropFilter: 'blur(16px)', borderBottom: `1px solid ${BR}`, padding: '12px 16px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          
          {/* LEFT: Logo & Brand Name */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 }}>
            <img 
              src="/icons/blue-sage-logo.png" 
              alt="Still Waters" 
              style={{ height: '36px', width: 'auto', objectFit: 'contain' }} 
            />
            <span style={{ fontFamily: serif, fontWeight: 700, fontSize: 'clamp(16px, 4vw, 20px)', color: TX, whiteSpace: 'nowrap' }}>
              Still Waters <span style={{ color: BLUE }}>Academy</span>
            </span>
          </Link>

          {/* RIGHT: Desktop Navigation Links */}
          <div className="hidden md:flex" style={{ alignItems: 'center', gap: 20 }}>
            <a href="#how-it-works" style={{ color: MU, fontSize: 13, fontWeight: 700, textDecoration: 'none', transition: 'color 0.15s' }}>
              {t('common.how_it_works', 'How it Works')}
            </a>
            <a href="#pricing" style={{ color: MU, fontSize: 13, fontWeight: 700, textDecoration: 'none', transition: 'color 0.15s' }}>
              Pricing
            </a>
            <a href="#faq" style={{ color: MU, fontSize: 13, fontWeight: 700, textDecoration: 'none', transition: 'color 0.15s' }}>
              FAQ
            </a>
            <Link to="/verification" style={{ color: MU, fontSize: 13, fontWeight: 700, textDecoration: 'none', transition: 'color 0.15s' }}>
              Verification
            </Link>
            <LanguagePicker />
            <button onClick={() => navigate('/platform/setup')}
              style={{ background: G, color: M, padding: '8px 20px', borderRadius: 999, fontSize: 13, fontWeight: 800, border: 'none', cursor: 'pointer', transition: 'opacity 0.15s' }}>
              Start Setup →
            </button>
          </div>

          {/* RIGHT: Mobile Controls */}
          <div className="flex md:hidden" style={{ alignItems: 'center', gap: 8 }}>
            <button onClick={() => navigate('/platform/setup')}
              style={{ background: G, color: M, padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 800, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              Setup →
            </button>
            <button
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label="Toggle navigation"
              style={{
                width: 38, height: 38, borderRadius: 10,
                border: `1px solid ${BR}`, background: 'rgba(255,255,255,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: TX, cursor: 'pointer',
              }}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Drawer */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden', borderTop: `1px solid ${BR}`, marginTop: 12, paddingTop: 16, paddingBottom: 16 }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <a
                  href="#how-it-works"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ color: TX, fontSize: 15, fontWeight: 600, textDecoration: 'none', padding: '6px 0' }}
                >
                  {t('common.how_it_works', 'How it Works')}
                </a>
                <a
                  href="#pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ color: TX, fontSize: 15, fontWeight: 600, textDecoration: 'none', padding: '6px 0' }}
                >
                  Pricing
                </a>
                <a
                  href="#faq"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ color: TX, fontSize: 15, fontWeight: 600, textDecoration: 'none', padding: '6px 0' }}
                >
                  FAQ
                </a>
                <Link
                  to="/verification"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ color: TX, fontSize: 15, fontWeight: 600, textDecoration: 'none', padding: '6px 0' }}
                >
                  Verification
                </Link>
                <div style={{ padding: '8px 0', borderTop: `1px solid ${BR}`, borderBottom: `1px solid ${BR}` }}>
                  <LanguagePicker />
                </div>
                <button
                  onClick={() => { setMobileMenuOpen(false); navigate('/platform/setup') }}
                  style={{ width: '100%', background: G, color: M, padding: '12px 20px', borderRadius: 12, fontSize: 14, fontWeight: 800, border: 'none', cursor: 'pointer', textAlign: 'center' }}
                >
                  Start Setup Now →
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ═══ HERO ═══ */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: '100px 24px 70px', textAlign: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.07, pointerEvents: 'none', background: `radial-gradient(ellipse at 50% 0%, ${G} 0%, transparent 55%)` }} />
        <div style={{ maxWidth: 860, margin: '0 auto', position: 'relative' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 999, background: 'rgba(212,168,67,0.08)', border: '1px solid rgba(212,168,67,0.25)', color: G, fontSize: 11, fontWeight: 800, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 30 }}>
            <Palette size={12} /> Your brand. Our engine. Verified evidence.
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            style={{ fontFamily: serif, fontSize: 'clamp(2.4rem, 6vw, 4.4rem)', fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.02em', margin: '0 0 24px', color: 'gold' }}>
            Launch your own learning platform<br />
            <span style={{ color: BLUE }}>without building one.</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
            style={{ color: MU, fontSize: 'clamp(15px, 2vw, 18px)', lineHeight: 1.7, maxWidth: 620, margin: '0 auto 14px' }}>
            Still Waters Academy powers your university, business, or nonprofit's education
            under your own brand: {catalogSize} courses, AI tutoring, server-verified assessment,
            and credentials any employer can verify in thirty seconds.
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.24 }}
            style={{ color: G, fontSize: 15, fontWeight: 700, margin: '0 auto 40px' }}>
            Your domain. Your logo. Your people. ${seatPrice} per seat per month.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/platform/setup')}
              style={{ padding: '18px 48px', borderRadius: 999, background: `linear-gradient(135deg, ${G}, #b8912e)`, color: M, fontWeight: 800, fontSize: 17, border: 'none', cursor: 'pointer', boxShadow: `0 8px 40px ${G}55` }}>
              Set Up Your Platform →
            </button>
            <Link to="/verification" style={{ background: 'none', border: 'none', color: MU, cursor: 'pointer', fontWeight: 700, fontSize: 13, padding: 0, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
              See what a verified credential looks like →
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
            style={{ marginTop: 56, paddingTop: 28, borderTop: `1px solid ${BR}`, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '32px 48px' }}>
            {[
              { v: `$${seatPrice}`, l: 'Per seat / month' },
              { v: `${catalogSize}+`, l: 'Audited courses' },
              { v: '30 sec', l: 'Employer verification' },
              { v: '5 min', l: 'Platform setup' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: serif, fontSize: 32, fontWeight: 800, color: G }}>{s.v}</div>
                <div style={{ fontSize: 11, color: MU2, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, marginTop: 4 }}>{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ ORG TYPES ═══ */}
      <section style={{ padding: '70px 24px', background: D2 }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 48px' }}>
            <p style={{ fontSize: 12, color: BLUE, fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', margin: '0 0 12px' }}>
              Who it's for
            </p>
            <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 700, margin: 0, color: TX }}>
              Any organization that teaches
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 16 }}>
            {ORG_TYPES.map((org, i) => (
              <motion.div key={org.value} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '26px 24px', display: 'flex', flexDirection: 'column', gap: 12, cursor: 'pointer' }}
                onClick={() => navigate(`/platform/setup?type=${org.value}`)}>
                <div style={{ width: 44, height: 44, borderRadius: 14, background: `${BLUE}15`, color: BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {org.icon}
                </div>
                <div>
                  <h3 style={{ fontSize: 16, fontWeight: 800, margin: '0 0 4px', color: TX }}>{org.label}</h3>
                  <p style={{ fontSize: 12.5, color: MU, lineHeight: 1.5, margin: 0 }}>{org.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how" style={{ padding: '70px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 48px' }}>
            <p style={{ fontSize: 12, color: BLUE, fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', margin: '0 0 12px' }}>
              The split
            </p>
            <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 700, margin: 0, color: TX }}>
              We handle the theory. You handle the human.
            </h2>
            <p style={{ color: MU, fontSize: 15, lineHeight: 1.7, margin: '14px auto 0', maxWidth: 520 }}>
              Your students learn theory on the platform, always current, AI-tutored, server-verified.
              Your lecturers and trainers spend their time on what only humans can do: discussion,
              practical work, mentorship, the moments that make teaching teaching.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 18 }}>
            {[
              { icon: <Palette size={20} />, title: 'Add your branding', desc: 'Your name, your logo, your colors, on your own subdomain or domain. Live in minutes.' },
              { icon: <Users size={20} />, title: 'Invite your lecturers', desc: 'They receive a magic link, generate or adopt courses, and see every student\'s actual understanding.' },
              { icon: <GraduationCap size={20} />, title: 'Onboard your students', desc: 'Students get a magic link too. They land signed in, learning, with evidence accumulating from lesson one.' },
              { icon: <Award size={20} />, title: 'Credentials issue automatically', desc: 'At 90% mastery, verified certificates issue under your institution\'s name, publicly verifiable by any employer.' },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 20, padding: '26px 24px' }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: `${BLUE}15`, color: BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  {s.icon}
                </div>
                <div style={{ color: G, fontSize: 11, fontWeight: 900, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 8 }}>Step {String(i + 1).padStart(2, '0')}</div>
                <h3 style={{ fontFamily: serif, fontSize: 17, fontWeight: 700, margin: '0 0 8px', color: TX }}>{s.title}</h3>
                <p style={{ color: MU, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ THE SUPPLEMENT PITCH ═══ */}
      <section style={{ padding: '60px 24px', background: D2 }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 999, background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.25)', color: '#4ade80', fontSize: 11, fontWeight: 800, marginBottom: 24 }}>
            <ShieldCheck size={12} /> The supplement, not the replacement
          </div>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.7rem, 4vw, 2.4rem)', fontWeight: 700, lineHeight: 1.15, margin: '0 0 18px', color: TX }}>
            Your lecturers keep doing what they do.<br />
            <span style={{ color: G }}>{t('common.now_with_ray_vision')}</span>
          </h2>
          <p style={{ color: MU, fontSize: 15.5, lineHeight: 1.7, margin: '0 auto 24px', maxWidth: 560 }}>
            The platform is their teaching assistant, never their replacement. Lecturers author
            courses, see every student's actual understanding before each session, and spend their
            time on the discussions, labs, and mentorship that no technology can replicate.
            Nothing about your existing structure changes, except your students learn more,
            your lecturers see more, and your credentials mean more.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
            {[
              'Content never goes stale',
              'Assessments can\'t be gamed by AI',
              'Credentials are verifiable by anyone',
              'Lecturers see every student\'s understanding',
              'Setup in one conversation',
            ].map((f, i) => (
              <span key={i} style={{ fontSize: 12, color: TX, fontWeight: 700, padding: '6px 14px', borderRadius: 999, background: 'rgba(255,255,255,0.04)', border: `1px solid ${BR}` }}>
                ✓ {f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" style={{ padding: '70px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <p style={{ fontSize: 12, color: BLUE, fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', margin: '0 0 16px' }}>
            Simple, transparent
          </p>
          <h2 style={{ fontFamily: serif, fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, margin: '0 0 12px', color: TX }}>
            ${seatPrice} per seat, per month
          </h2>
          <p style={{ color: MU, fontSize: 16, lineHeight: 1.7, margin: '0 auto 32px', maxWidth: 480 }}>
            A full year of personal AI training for every learner costs less than a single
            instructor-led training day. Schools and registered NGOs: ${schoolPrice}/seat.
            Admins and trainers included free. Regional pricing and annual contracts on request.
          </p>
          <div style={{ background: 'rgba(212,168,67,0.05)', border: `1.5px solid rgba(212,168,67,0.25)`, borderRadius: 20, padding: '28px 32px', textAlign: 'left' }}>
            {[
              'Full access to the audited course catalog',
              'SyllabusGenerator for custom courses',
              'Personal AI tutor per student',
              'Server-verified assessment (tamper-resistant)',
              'Verified, publicly confirmable credentials',
              'Your branding, your domain, your lecturers',
              'Evidence transcripts for any employer',
            ].map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: TX, fontSize: 13.5, fontWeight: 600, marginBottom: 10 }}>
                <CheckCircle2 size={16} color={G} style={{ flexShrink: 0, marginTop: 1 }} /> {f}
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/platform/setup')}
            style={{ marginTop: 28, padding: '16px 48px', borderRadius: 999, background: G, color: M, fontWeight: 800, fontSize: 16, border: 'none', cursor: 'pointer', boxShadow: `0 8px 30px ${G}44` }}>
            Start Your Platform →
          </button>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section id="faq" style={{ padding: '60px 24px 80px', background: D2 }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.7rem, 4vw, 2.4rem)', fontWeight: 700, margin: 0, color: TX }}>
              What organizations ask
            </h2>
          </div>
          {[
            { q: 'Does this replace our lecturers or degrees?', a: 'No, this is a supplement, not a replacement. Your lecturers author the courses, your students learn the theory on the platform, and your lecturers spend their valuable time on discussion, practical work, and mentorship. Your degrees stay yours. The platform makes your existing structure more effective.' },
            { q: `What does "$${seatPrice} per seat per month" include?`, a: 'Full access to the course catalog, a personal AI tutor for every learner, server-verified assessment, verified credentials under your institution\'s name, the evidence transcript system, your branded portal, and admin accounts. There are no per-course or per-assessment fees.' },
            { q: 'Can we use our own domain?', a: 'Yes. You can use a subdomain ({yourname}.stillwatersacademy.com, instant) or your own domain (learn.youruniversity.edu, one CNAME record from your IT team). Both take under 5 minutes.' },
            { q: 'How do our students\' credentials work?', a: 'When a student reaches 90% mastery in a course, a verified certificate issues automatically under your institution\'s name. Each certificate has a public verification page where any employer can confirm authenticity, and with the student\'s PIN, see the complete learning evidence: every assessment, every score, every retry.' },
            { q: 'What about content going stale?', a: 'Your lecturers can regenerate or edit any course at any time. The course catalog is audited and maintained. If your field changes, the syllabus regenerates, content is renewable, while the evidence record is permanent.' },
            { q: 'How long does setup take?', a: 'Branding: 5 minutes. Course selection or generation: one sitting. Inviting lecturers: they receive a magic link and are working within minutes. Students: same magic-link onboarding. The full platform can be live the same day you say yes.' },
          ].map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} defaultOpen={i === 0} />
          ))}
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ borderTop: `1px solid ${BR}`, padding: '36px 24px', display: 'flex', justifyContent: 'center', gap: 28, flexWrap: 'wrap' }}>
        {[
          { l: 'Families', to: '/homeschool' },
          { l: 'Schools', to: '/schools' },
          { l: 'Churches', to: '/church' },
          { l: 'Verification', to: '/verification' },
          { l: 'Privacy', to: '/privacy' },
          { l: 'Terms', to: '/terms' },
        ].map(l => (
          <Link key={l.l} to={l.to} style={{ color: MU2, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', textDecoration: 'none' }}>{l.l}</Link>
        ))}
      </footer>
    </div>
  )
}

function FaqItem({ q, a, defaultOpen }: { q: string; a: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(!!defaultOpen)
  return (
    <div style={{ marginBottom: 10 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', textAlign: 'left', padding: '18px 22px', background: open ? 'rgba(255,255,255,0.03)' : 'transparent', border: `1px solid ${open ? 'rgba(212,168,67,0.25)' : BR}`, borderRadius: 16, cursor: 'pointer', color: TX, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{q}</span>
        <span style={{ color: G, fontSize: 20, fontWeight: 300 }}>{open ? '−' : '+'}</span>
      </button>
      {open && <div style={{ padding: '16px 22px 4px', color: MU, fontSize: 14, lineHeight: 1.7 }}>{a}</div>}
    </div>
  )
}
